import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { formatDate } from "../../services/helpers";
import { HistoryPositionCode } from "../../interfaces/positionCode";
import { getPositionCodeHistory } from "../../services/positionCodeService";

interface PositionCodesHistoryTableProps {
  client: Client;
}

const PositionCodesHistoryTable: React.FC<PositionCodesHistoryTableProps> = ({
  client,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [positionCodes, setPositionCodes] = useState<HistoryPositionCode[]>([]);

  useEffect(() => {
    const getHistory = async () => {
      try {
        setLoadingSpinner(true);

        const historyResponse = await getPositionCodeHistory(client.clientID);
        setPositionCodes(historyResponse);
      } catch (error) {
        console.error("Error getting position code history:", error);
        setErrorMessage("Error getting position code history.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    getHistory();
  }, []);

  return (
    <>
      <div className="table-wrapper">
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="table-container" style={{ fontSize: "large" }}>
          <table className="table" style={{ cursor: "pointer" }}>
            <thead>
              <tr>
                <th style={{ width: "110px" }} title={"Position Code ID"}>
                  Position Code Number
                </th>
                <th style={{ width: "260px" }} title={"Title"}>
                  Title
                </th>
                <th style={{ width: "100px" }} title={"FCRA"}>
                  FCRA
                </th>
                <th style={{ width: "100px" }} title={"Criminal Lookback"}>
                  Criminal Lookback
                </th>
                <th style={{ width: "100px" }} title={"Reference Lookback"}>
                  Reference Lookback
                </th>
                <th
                  style={{ width: "100px" }}
                  title={"Requires Candidate Payment"}
                >
                  Requires Candidate Payment
                </th>
                <th style={{ width: "360px" }} title={"Services"}>
                  Services
                </th>
                <th style={{ width: "220px" }} title={"PC Event"}>
                  PC Event
                </th>
                <th style={{ width: "220px" }} title={"Modified By (PC)"}>
                  Modified By (PC)
                </th>
                <th style={{ width: "200px" }} title={"Modified At (PC)"}>
                  Modified At (PC)
                </th>
                <th style={{ width: "600px" }} title={"PCS Event"}>
                  PCS Event
                </th>
                <th style={{ width: "220px" }} title={"Modified By (Services)"}>
                  Modified By (Services)
                </th>
                <th style={{ width: "200px" }} title={"Modified At (Services)"}>
                  Modified At (Services)
                </th>
              </tr>
            </thead>

            <tbody>
              {positionCodes?.length === 0 ? (
                <tr>
                  <td colSpan={17} style={{ textAlign: "center" }}>
                    No history found.
                  </td>
                </tr>
              ) : (
                positionCodes?.map((row, index) => {
                  const servicesText =
                    row.services?.length > 0
                      ? row.services
                          .map((s) => s.serviceName ?? `Service ${s.serviceID}`)
                          .join(", ")
                      : "";

                  return (
                    <tr
                      key={`${row.positionCodeID}-${row.sysStartTimePC}-${index}`}
                    >
                      <td>{row.positionCodeNumber}</td>
                      <td
                        className="ellipsis-cell"
                        title={row.positionCodeTitle ?? ""}
                      >
                        {row.positionCodeTitle}
                      </td>
                      <td>{row.fcraCompliance ? "Yes" : "No"}</td>
                      <td>{row.criminalLookbackYears ?? ""}</td>
                      <td>{row.referenceLookbackYears ?? ""}</td>
                      <td>{row.requiresCandidatePayment ? "Yes" : "No"}</td>
                      <td className="ellipsis-cell" title={servicesText}>
                        {servicesText}
                      </td>
                      <td>{row.eventPC}</td>
                      <td
                        className="ellipsis-cell"
                        title={row.modifiedByPC ?? ""}
                      >
                        {row.modifiedByPC}
                      </td>
                      <td>{formatDate(row.modifiedAtPC, "datetime")}</td>
                      <td className="ellipsis-cell" title={row.eventPCS}>
                        {row.eventPCS}
                      </td>
                      <td
                        className="ellipsis-cell"
                        title={row.modifiedByPCS ?? ""}
                      >
                        {row.modifiedByPCS}
                      </td>
                      <td>{formatDate(row.modifiedAtPCS, "datetime")}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {loadingSpinner && (
          <div className="loading-mask">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default PositionCodesHistoryTable;
